import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUserRoleList, handleUserRole } from '../../../Redux/UserSlice';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { useForm } from 'react-hook-form';
import { AddUserAccess, EditUserRole } from '../../Common/Common';
import Loading from '../../Common/Loading';

export default function AddEditUserRole({ toggleModal, userRoleData, fetchUserData }) {
    const dispatch = useDispatch();
    const { handleSubmit, register, formState: { errors }, setValue, watch } = useForm();
    const [roleList, setRoleList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (userRoleData) {
            setValue('name', userRoleData?.userRole);
            const userAccess = EditUserRole(userRoleData);

            userAccess.forEach(item => {
                setValue(`${item?.pageName}_View`, item[`${item?.pageName}_View`] || false);
                setValue(`${item?.pageName}_Edit`, item[`${item?.pageName}_Edit`] || false);
                setValue(`${item?.pageName}_Delete`, item[`${item?.pageName}_Delete`] || false);
            });
            setRoleList(userAccess);
        }
    }, [userRoleData, setValue]);

    useEffect(() => {
        setLoading(true);
        dispatch(getUserRoleList({}))
            .then((res) => {
                setRoleList(res?.payload?.data || []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [dispatch]);

    const onSubmit = (data) => {
        const userAccess = AddUserAccess(roleList, watch);

        const params = {
            UsersRoleID: userRoleData?.usersRoleID || 0,
            UserRole: data?.name,
            isActive: 1,
            userID: 0,
            mode: "Save",
            userCount: 0,
            useraccess: userAccess,
        };

        dispatch(handleUserRole(params))
            .then(() => {
                toggleModal();
                fetchUserData();
            });
    };

    const checkboxClass =
        'h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-100';

    const renderRoleItems = (items, parentId = 0) => {
        const filteredItems = items?.length > 0 ? items?.filter(item => item?.parentID === parentId) : [];

        return filteredItems.map(item => {
            const hasChildren = items?.some(child => child?.parentID === item?.moduleID);
            const isGroupHeader = item?.parentID === 0 && hasChildren;
            const showCheckboxes = !isGroupHeader && item?.pageName !== "Log Out";

            return (
                <React.Fragment key={item?.moduleID}>
                    <tr className={isGroupHeader ? 'bg-slate-50/80' : ''}>
                        <td className={`font-medium ${isGroupHeader ? 'text-slate-800 font-semibold' : 'text-slate-600'}`}>
                            <span style={{ paddingLeft: item?.parentID !== 0 ? '1.25rem' : 0 }}>
                                {item?.pageName}
                            </span>
                        </td>
                        {showCheckboxes ? (
                            <>
                                <td className="center">
                                    <input
                                        id={`${item?.pageName}_View`}
                                        {...register(`${item?.pageName}_View`)}
                                        className={checkboxClass}
                                        type="checkbox"
                                        onChange={() => {
                                            if (watch(`${item?.pageName}_View`) === true) {
                                                setValue(`${item?.pageName}_Delete`, false);
                                                setValue(`${item?.pageName}_Edit`, false);
                                            }
                                        }}
                                    />
                                </td>
                                <td className="center">
                                    <input
                                        id={`${item?.pageName}_Edit`}
                                        {...register(`${item?.pageName}_Edit`)}
                                        className={checkboxClass}
                                        type="checkbox"
                                        onChange={() => {
                                            if (watch(`${item?.pageName}_Edit`)) {
                                                setValue(`${item?.pageName}_Delete`, false);
                                            } else {
                                                setValue(`${item?.pageName}_View`, true);
                                            }
                                        }}
                                    />
                                </td>
                                <td className="center">
                                    <input
                                        id={`${item?.pageName}_Delete`}
                                        {...register(`${item?.pageName}_Delete`)}
                                        className={checkboxClass}
                                        type="checkbox"
                                        onChange={() => {
                                            setValue(`${item?.pageName}_View`, true);
                                            setValue(`${item?.pageName}_Edit`, true);
                                        }}
                                    />
                                </td>
                            </>
                        ) : (
                            <>
                                <td className="center"></td>
                                <td className="center"></td>
                                <td className="center"></td>
                            </>
                        )}
                    </tr>
                    {hasChildren && renderRoleItems(items, item?.moduleID)}
                </React.Fragment>
            );
        });
    };

    const isEdit = Boolean(userRoleData?.usersRoleID);

    return (
        <div
            id="default-modal"
            tabIndex="-1"
            aria-hidden="true"
            className="fixed inset-0 z-[9999] flex min-h-screen w-full items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
        >
            <div className="w-full max-w-xl max-h-[calc(100vh-200px)]">
                <div className="relative flex max-h-[calc(100vh-200px)] flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-2xl">
                    <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50/50 px-5 py-4">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800">
                                {isEdit ? 'Update User Role' : 'Add User Role'}
                            </h3>
                            <p className="mt-0.5 text-xs text-slate-500">
                                Define role name and assign module permissions
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => toggleModal()}
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                            <AiOutlineCloseCircle className="text-2xl" />
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex h-96 items-center justify-center">
                            <Loading />
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
                            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 vertical-scroll-inner">
                                <div className="mb-5">
                                    <label
                                        htmlFor="name"
                                        className="mb-1.5 block text-xs font-bold tracking-wide text-slate-600"
                                    >
                                        Role Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        placeholder="Enter role name"
                                        className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm capitalize text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                        {...register("name", { required: "Role Name is Required" })}
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-xs font-semibold text-red-500">
                                            {errors?.name?.message}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <label className="text-xs font-bold tracking-wide text-slate-600">
                                            Module Permissions
                                        </label>
                                        <span className="text-[11px] text-slate-400">
                                            View · Create & Edit · Delete
                                        </span>
                                    </div>
                                    <div className="crm-table-wrapper">
                                        <table className="crm-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Module</th>
                                                    <th scope="col" className="center">View</th>
                                                    <th scope="col" className="center">Create & Edit</th>
                                                    <th scope="col" className="center">Delete</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderRoleItems(roleList)}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-slate-50/50 px-5 py-4">
                                <button
                                    type="button"
                                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100"
                                    onClick={() => toggleModal()}
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition-all hover:bg-indigo-700 hover:shadow-lg"
                                >
                                    {isEdit ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
